'use client';

import { Suspense, useEffect, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useSearchParams, useRouter } from 'next/navigation';
import { apiClient, ApiClientError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Image from 'next/image';

function SignInForm() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code' | 'request-access' | 'request-submitted'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Access request form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');

  const searchParams = useSearchParams();
  const router = useRouter();
  const callbackUrl = searchParams.get('callbackUrl') || '/';

  // Pré-remplissage depuis le lien email d'invitation
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) setEmail(emailParam);
  }, [searchParams]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { result } = await apiClient.post<{ sent: boolean; unknown_email?: boolean }>('/api/auth/send-code', { email });

      if (result.unknown_email) {
        // Email not in system — show access request form
        setStep('request-access');
      } else {
        setStep('code');
        setCooldown(60);
        const interval = setInterval(() => {
          setCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.response.status === 429) {
          setError('Trop de tentatives. Veuillez réessayer plus tard.');
        } else {
          setError(err.response.detail || "Erreur lors de l'envoi du code");
        }
      } else {
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn('code', {
        email,
        code,
        redirect: false,
      });

      if (result?.error) {
        setError('Code invalide ou expiré');
        setCode('');
      } else if (result?.ok) {
        // Toujours rediriger vers / qui redirigera selon le rôle
        router.push('/');
        router.refresh();
      }
    } catch (err) {
      setError('Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await apiClient.post('/api/auth/request-access', {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        company_name: companyName,
      });

      setStep('request-submitted');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.response.detail || 'Erreur lors de la demande');
      } else {
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Image src="/images/logo-pluo.svg" alt="PLUO" width={64} height={64} />
          </div>
          <CardTitle>
            {{ email: 'Connexion', code: 'Connexion', 'request-access': "Demande d'accès", 'request-submitted': 'Demande envoyée' }[step]}
          </CardTitle>
          <CardDescription>
            {{
              email: 'Entrez votre adresse email pour recevoir un code de connexion',
              code: 'Entrez le code de 6 chiffres envoyé par email',
              'request-access': 'Complétez vos informations pour demander un accès à la plateforme',
              'request-submitted': 'Votre demande a bien été envoyée',
            }[step]}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Envoyer le code'}
              </Button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                  required
                  disabled={loading}
                  autoFocus
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? 'Vérification...' : 'Se connecter'}
              </Button>
              <div className="text-center text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError('');
                  }}
                  className="text-primary hover:underline"
                  disabled={loading}
                >
                  Changer d&apos;email
                </button>
                {cooldown === 0 ? (
                  <span>
                    {' · '}
                    <button
                      type="button"
                      onClick={() => {
                        setStep('email');
                        setError('');
                        handleSendCode({ preventDefault: () => {} } as React.FormEvent);
                      }}
                      className="text-primary hover:underline"
                      disabled={loading}
                    >
                      Renvoyer le code
                    </button>
                  </span>
                ) : (
                  <span>{` · Renvoyer dans ${cooldown}s`}</span>
                )}
              </div>
            </form>
          )}

          {step === 'request-access' && (
            <form onSubmit={handleRequestAccess} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                Votre adresse email n&apos;est pas encore enregistrée. Remplissez ce formulaire pour demander un accès. Un gestionnaire de dossier validera votre demande et vous recevrez un email de confirmation.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    type="text"
                    placeholder="Jean"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    type="text"
                    placeholder="Dupont"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="email_display">Email</Label>
                <Input
                  id="email_display"
                  type="email"
                  value={email}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="phone">Téléphone (facultatif)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+32123456789"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="company_name">Nom d&apos;entreprise</Label>
                <Input
                  id="company_name"
                  type="text"
                  placeholder="Ma Société SA"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Envoi en cours...' : "Envoyer la demande d'accès"}
              </Button>

              <div className="text-center text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setError('');
                  }}
                  className="text-primary hover:underline"
                  disabled={loading}
                >
                  Retour
                </button>
              </div>
            </form>
          )}

          {step === 'request-submitted' && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-gray-700">
                Votre demande d&apos;accès a bien été transmise. Un gestionnaire de dossier examinera votre demande et vous recevrez un email de confirmation une fois votre accès validé.
              </p>
              <p className="text-sm text-gray-500">
                Email : <strong>{email}</strong>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Chargement...</div>}>
      <SignInForm />
    </Suspense>
  );
}
